import React, { useState } from 'react';
import { useLanguageStore } from '../../stores/languageStore';
import { formatFullWeekdayDate } from '../../utils/formatters';
import type { RelanceAgendaEntry } from '../../types/customerFollowUp/relanceAgenda';
import type { ForecastDay } from '../../utils/relance/forecastDays';
import { ExpectedRelanceModal } from './ExpectedRelanceModal';
import { RelanceActionModal } from './RelanceActionModal';
import { RelanceForecastRow } from './RelanceForecastRow';

interface RelanceForecastProps {
    /** Days without a relance are left out. */
    days: ForecastDay[];
    canCollectPayment: boolean;
    testId?: string;
}

// One grid spans every day so the tags and amounts line up in columns across the day groups.
export const RelanceForecast: React.FC<RelanceForecastProps> = ({ days, canCollectPayment, testId }) => {
    const lang = useLanguageStore(s => s.lang);
    const [openId, setOpenId] = useState<string | null>(null);
    // Looked up on every render so the modal closes by itself once the relance leaves the forecast.
    const openEntry = days.flatMap(d => d.entries).find(e => e.item.id === openId);
    // An expected relance offers only a collection, so it opens only where one is possible.
    const canOpen = (entry: RelanceAgendaEntry) =>
        entry.status !== 'expected' || (canCollectPayment && entry.item.customerExternalId !== null);

    return (
        <>
            <div data-testid={testId} className="grid grid-cols-relance-agenda gap-x-xl gap-y-2xl">
                {days.map(day => (
                    <section key={day.key} aria-labelledby={`forecast-day-${day.key}`} data-testid={`home-upcoming-day-${day.key}`} className="col-span-full grid grid-cols-subgrid gap-y-s">
                        <h3 id={`forecast-day-${day.key}`} className="col-span-full text-s font-semibold text-content">
                            {formatFullWeekdayDate(day.key, lang)}
                        </h3>
                        <ul role="list" className="col-span-full grid grid-cols-subgrid">
                            {day.entries.map(entry => (
                                <RelanceForecastRow key={entry.item.id} entry={entry} onOpen={canOpen(entry) ? () => setOpenId(entry.item.id) : undefined} />
                            ))}
                        </ul>
                    </section>
                ))}
            </div>
            {openEntry?.status === 'expected' && <ExpectedRelanceModal entry={openEntry} onClose={() => setOpenId(null)} />}
            {openEntry && openEntry.status !== 'expected' && (
                <RelanceActionModal entry={openEntry} canCollectPayment={canCollectPayment} onClose={() => setOpenId(null)} />
            )}
        </>
    );
};
